/**
 * Styles for the feedback screens.
 *
 * Tokens, resets, buttons, inputs, pills and the modal all come from the
 * invoice module's kit, which is already the shared design kit for anything
 * outside the MUI theme. This sheet adds only what the feedback screens
 * need on top, namespaced `.fb-` and scoped to `.iv-root` like the rest.
 */

export const FEEDBACK_CSS = `
/* --- the dialog ---------------------------------------------------- *
   A red button that opens a teal-and-grey form reads as two unrelated
   things. The band and the mark carry the launcher's colour inside, and
   stop at the header: red through a whole form is alarming. */
.iv-modal--feedback .iv-modal__head {
  background: linear-gradient(180deg, #FFF5F5 0%, #FFFFFF 100%);
  border-bottom-color: #F6DADA;
}
.iv-modal--feedback .iv-modal__title { font-size: 19px; letter-spacing: -.015em; }
.iv-modal--feedback .iv-modal__sub { font-size: 13.5px; }

.fb-hero {
  display: flex; align-items: flex-start; gap: 10px; margin: 0;
  font-size: 13px; line-height: 1.55; color: var(--ink-3);
}
.fb-hero__mark {
  flex: 0 0 auto; width: 30px; height: 30px; border-radius: 9px;
  display: grid; place-items: center; background: #FFF0F0; color: #CC0000;
}

/* --- field rhythm --------------------------------------------------- *
   The kit's label is uppercase 800 with wide tracking, which is right for
   a dense money console and wrong for five fields asking someone a
   favour. Scoped to this form so the invoice screens keep theirs. */
.fb-form .iv-label, .fb-group__legend {
  font-size: 13px; font-weight: 750; letter-spacing: 0; text-transform: none;
  color: var(--ink); margin-bottom: 7px; padding: 0; display: block; width: 100%;
}
.fb-group { border: 0; padding: 0; margin: 0; min-width: 0; }
.fb-form .iv-input, .fb-form .iv-textarea { font-size: 14.5px; padding: 11px 13px; }
.fb-form .iv-textarea { line-height: 1.6; }

/* --- choice cards (type and impact) --------------------------------- */
.fb-choices { display: grid; gap: 9px; grid-template-columns: repeat(3, minmax(0, 1fr)); }
.fb-choice {
  all: unset; box-sizing: border-box; cursor: pointer; position: relative;
  display: flex; flex-direction: column; gap: 3px;
  border: 1px solid var(--line-2); border-radius: 12px; background: var(--surface);
  padding: 11px 12px 12px; min-height: 44px;
  transition: border-color .15s ease, background .15s ease, box-shadow .15s ease;
}
.fb-choice:hover { border-color: var(--ink-4); background: #FCFCFD; }
.fb-choice:focus-visible { outline: 3px solid rgba(30,156,178,.4); outline-offset: 2px; }
.fb-choice__top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 7px; }
.fb-choice__icon {
  width: 30px; height: 30px; border-radius: 9px; display: grid; place-items: center;
  background: var(--line); color: var(--ink-3);
  transition: background .15s ease, color .15s ease;
}
.fb-choice__tick {
  width: 18px; height: 18px; border-radius: 50%; display: grid; place-items: center;
  background: currentColor; color: inherit;
}
.fb-choice__tick svg { color: #fff; }
.fb-choice__label { font-size: 13.5px; font-weight: 800; color: var(--ink); }
.fb-choice__hint { font-size: 11.5px; line-height: 1.45; color: var(--ink-3); }

/* Selected says so three ways -- fill, border and tick -- so it never
   rests on colour alone. */
.fb-choice--red[aria-pressed="true"]   { background: #FEF2F2; border-color: #DC2626; color: #B91C1C; }
.fb-choice--amber[aria-pressed="true"] { background: #FFFBEB; border-color: #D97706; color: #A15C07; }
.fb-choice--blue[aria-pressed="true"]  { background: #EFF6FF; border-color: #2563EB; color: #1D4ED8; }
.fb-choice--grey[aria-pressed="true"]  { background: var(--canvas); border-color: var(--ink-3); color: var(--ink-2); }
/* The wrapper keeps the card's colour, so currentColor resolves to the
   tone; only the glyph inside turns white. Setting color here as well
   would make the background white too and the icon would vanish. */
.fb-choice[aria-pressed="true"] .fb-choice__icon { background: currentColor; }
.fb-choice[aria-pressed="true"] .fb-choice__icon svg { color: #fff; }
.fb-choice[aria-pressed="true"] .fb-choice__hint { color: currentColor; opacity: .85; }

/* --- severity meter -------------------------------------------------- */
.fb-meter { display: flex; align-items: flex-end; gap: 3px; height: 30px; }
.fb-meter__bar { width: 5px; border-radius: 2px; background: var(--line-2); }
.fb-meter__bar:nth-child(1) { height: 9px; }
.fb-meter__bar:nth-child(2) { height: 15px; }
.fb-meter__bar:nth-child(3) { height: 21px; }
.fb-meter__bar--on { background: var(--ink-4); }
.fb-choice--red[aria-pressed="true"] .fb-meter__bar--on   { background: #DC2626; }
.fb-choice--amber[aria-pressed="true"] .fb-meter__bar--on { background: #D97706; }
.fb-choice--grey[aria-pressed="true"] .fb-meter__bar--on  { background: var(--ink-2); }

/* --- form rhythm ----------------------------------------------------- */
.fb-form { display: grid; gap: 20px; }
.fb-count { float: right; font-size: 11.5px; font-weight: 700; color: var(--ink-4); letter-spacing: 0; text-transform: none; }
.fb-count--low { color: #B45309; }
.fb-context { display: flex; align-items: flex-start; gap: 9px; border-radius: var(--radius-sm); background: var(--canvas); border: 1px solid var(--line); padding: 10px 12px; }
.fb-context__text { font-size: 12.5px; line-height: 1.5; color: var(--ink-3); margin: 0; }
.fb-context__screen { font-weight: 800; color: var(--ink-2); }

/* --- screenshots ---------------------------------------------------- */
.fb-optional { font-size: 11px; font-weight: 700; letter-spacing: 0; text-transform: none; color: var(--ink-4); margin-left: 4px; }
.fb-shots { display: grid; gap: 7px; margin-bottom: 8px; }
.fb-shot { display: flex; align-items: center; gap: 10px; border: 1px solid var(--line); border-radius: var(--radius-sm); background: var(--surface); padding: 7px 7px 7px 8px; }
.fb-shot__img { flex: 0 0 auto; width: 46px; height: 34px; border-radius: 6px; object-fit: cover; background: var(--canvas); border: 1px solid var(--line); display: block; }
.fb-shot__meta { flex: 1 1 auto; min-width: 0; display: grid; }
.fb-shot__name { font-size: 12.5px; font-weight: 700; color: var(--ink-2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fb-shot__size { font-size: 11.5px; color: var(--ink-4); }
.fb-shot__x { all: unset; cursor: pointer; flex: 0 0 auto; display: inline-grid; place-items: center; min-width: 44px; min-height: 44px; margin: -7px -7px -7px 0; color: var(--ink-4); border-radius: 8px; }
.fb-shot__x:hover { color: #B91C1C; background: #FEF2F2; }
.fb-shot__x:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }

.fb-drop {
  display: flex; align-items: center; gap: 9px; min-height: 44px;
  border: 1px dashed var(--line-2); border-radius: var(--radius-sm);
  background: var(--canvas); color: var(--ink-3); padding: 10px 12px;
  transition: border-color .15s ease, background .15s ease;
}
.fb-drop--over { border-color: var(--accent); background: var(--accent-bg); color: var(--accent-ink); }
.fb-drop__text { font-size: 12.5px; line-height: 1.5; }

/* --- attached, on a saved item --------------------------------------- */
.fb-seen { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
.fb-seen__link { display: block; border-radius: 8px; border: 1px solid var(--line); overflow: hidden; line-height: 0; }
.fb-seen__link:hover { border-color: var(--accent); }
.fb-seen__link:focus-visible { outline: 3px solid rgba(30,156,178,.4); outline-offset: 2px; }
.fb-seen__img { width: 92px; height: 64px; object-fit: cover; display: block; background: var(--canvas); }

/* --- pills and tags ------------------------------------------------ */
.fb-tag {
  display: inline-flex; align-items: center; gap: 5px; border-radius: 6px; padding: 2px 7px;
  font-size: 11.5px; font-weight: 800; letter-spacing: .01em; white-space: nowrap;
}
.fb-tag--red    { color: #B91C1C; background: #FEE2E2; }
.fb-tag--amber  { color: #A15C07; background: #FEF3C7; }
.fb-tag--blue   { color: #1D4ED8; background: #DBEAFE; }
.fb-tag--grey   { color: var(--ink-2); background: var(--line); }

/* --- item rows ----------------------------------------------------- */
/* minmax(0,...) so a track can never be forced wider than the card list by
   its own content. Without it an unbroken string sets the track's min-content
   width and the whole page grows sideways. */
.fb-list { display: grid; grid-template-columns: minmax(0, 1fr); gap: 10px; }
.fb-item {
  all: unset; box-sizing: border-box; cursor: pointer; display: block; width: 100%;
  /* Set here rather than per field because every line in this card is text
     somebody typed -- title, detail, their name, the owner. overflow-wrap
     inherits, so a field added later is covered without anyone remembering.
     Not overflow-x: hidden, which would clip the text instead of wrapping it.
     anywhere rather than break-word, so the long string also stops counting
     towards min-content width, which is what actually widened the page. */
  min-width: 0; overflow-wrap: anywhere;
  border: 1px solid var(--line); border-radius: var(--radius); background: var(--surface);
  padding: 14px 16px; box-shadow: var(--shadow-sm);
  transition: border-color .15s ease, box-shadow .15s ease, transform .12s ease;
}
.fb-item:hover { border-color: var(--line-2); box-shadow: var(--shadow-md); }
.fb-item:focus-visible { outline: 3px solid rgba(30,156,178,.4); outline-offset: 2px; }
.fb-item[aria-current="true"] { border-color: var(--accent); box-shadow: inset 0 0 0 1px var(--accent); }
.fb-item__top { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 5px; }
.fb-item__title { font-size: 14.5px; font-weight: 750; color: var(--ink); margin: 0 0 4px; line-height: 1.4; overflow-wrap: anywhere; }
.fb-item__detail { font-size: 13px; line-height: 1.55; color: var(--ink-3); margin: 0; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.fb-item__foot { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-top: 9px; font-size: 12px; color: var(--ink-4); }
.fb-item__dot { width: 7px; height: 7px; border-radius: 50%; background: var(--accent); flex: none; }

/* --- reply --------------------------------------------------------- */
.fb-reply { margin-top: 11px; border-left: 3px solid var(--accent); background: var(--accent-bg); border-radius: 0 var(--radius-sm) var(--radius-sm) 0; padding: 10px 13px; }
.fb-reply__who { font-size: 11.5px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; color: var(--accent-ink); margin: 0 0 3px; }
.fb-reply__text { font-size: 13px; line-height: 1.6; color: var(--ink-2); margin: 0; white-space: pre-wrap; overflow-wrap: anywhere; }

/* --- board layout --------------------------------------------------- */
.fb-board { display: grid; grid-template-columns: minmax(0, 1fr) 380px; gap: 20px; align-items: start; }
.fb-board__side { position: sticky; top: 20px; display: grid; gap: 14px; }
.fb-panel { border: 1px solid var(--line); border-radius: var(--radius); background: var(--surface); padding: 16px; box-shadow: var(--shadow-sm); }
.fb-panel__title { font-size: 12px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; color: var(--ink-3); margin: 0 0 11px; }
.fb-bar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 16px; }
.fb-bar__grow { flex: 1 1 220px; min-width: 180px; }
.fb-bar .iv-select { width: auto; min-width: 128px; padding-top: 9px; padding-bottom: 9px; }

/* --- state tabs ------------------------------------------------------ */
.fb-tabs { display: flex; gap: 4px; flex-wrap: wrap; margin-bottom: 14px; border-bottom: 1px solid var(--line); }
.fb-tab {
  all: unset; box-sizing: border-box; cursor: pointer; display: inline-flex; align-items: center; gap: 7px;
  padding: 9px 13px; min-height: 44px; font-size: 13px; font-weight: 700; color: var(--ink-3);
  border-bottom: 2px solid transparent; margin-bottom: -1px;
}
.fb-tab:hover { color: var(--ink); }
.fb-tab:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; border-radius: 6px; }
.fb-tab[aria-selected="true"] { color: var(--ink); border-bottom-color: var(--accent); }
.fb-tab__n { font-size: 11.5px; font-weight: 800; background: var(--line); color: var(--ink-2); border-radius: 999px; padding: 1px 7px; }
.fb-tab[aria-selected="true"] .fb-tab__n { background: var(--accent-bg); color: var(--accent-ink); }

/* --- screen rollup ---------------------------------------------------- */
.fb-rollup { display: grid; gap: 2px; }
.fb-rollup__row { all: unset; box-sizing: border-box; cursor: pointer; display: flex; align-items: center; gap: 9px; padding: 8px 9px; min-height: 44px; border-radius: 8px; }
.fb-rollup__row:hover { background: var(--canvas); }
.fb-rollup__row:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
.fb-rollup__row[aria-pressed="true"] { background: var(--accent-bg); }
.fb-rollup__name { flex: 1 1 auto; min-width: 0; font-size: 13px; font-weight: 650; color: var(--ink-2); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fb-rollup__n { font-size: 12px; font-weight: 800; color: var(--ink-3); font-variant-numeric: tabular-nums; }
.fb-rollup__bar { flex: 0 0 56px; height: 6px; border-radius: 3px; background: var(--line); overflow: hidden; }
.fb-rollup__fill { height: 100%; background: var(--accent); border-radius: 3px; }

/* --- triage ------------------------------------------------------------ */
.fb-triage { display: grid; gap: 14px; }
.fb-triage__meta { display: grid; grid-template-columns: auto minmax(0, 1fr); gap: 5px 12px; font-size: 12.5px; }
.fb-triage__key { color: var(--ink-4); font-weight: 700; }
.fb-triage__val { color: var(--ink-2); overflow-wrap: anywhere; }
.fb-triage__body { font-size: 13.5px; line-height: 1.65; color: var(--ink-2); white-space: pre-wrap; overflow-wrap: anywhere; margin: 0; }

/* --- misc --------------------------------------------------------------- */
.fb-note { font-size: 12.5px; line-height: 1.55; color: var(--ink-3); margin: 0; }
.fb-ok { display: grid; gap: 14px; justify-items: center; text-align: center; padding: 8px 0 4px; }
.fb-ok__mark { width: 46px; height: 46px; border-radius: 50%; display: grid; place-items: center; background: #DCFCE7; color: #15803D; }

@media (max-width: 1100px) {
  .fb-board { grid-template-columns: minmax(0, 1fr); }
  .fb-board__side { position: static; }
}
@media (max-width: 640px) {
  .fb-choices { grid-template-columns: minmax(0, 1fr); }
  .fb-choice { flex-direction: row; align-items: center; gap: 11px; padding: 10px 12px; }
  .fb-choice__top { margin-bottom: 0; flex: 0 0 auto; }
  .fb-choice__label { flex: 1 1 auto; }
  /* Three stacked cards with two lines each is most of a phone screen. */
  .fb-choice__hint { display: none; }
  .fb-bar .iv-select { width: 100%; }
}
`;
